import { useState } from "react";
import { toast } from "sonner";
import { Heart, GraduationCap, Users, Sprout, Loader2 } from "lucide-react";
import { startCheckout, formatApiError } from "@/lib/api";

const CAMPAIGNS = [
  { Icon: GraduationCap, title: "Girls' Education Fund", raised: 68, goal: "Send 1,000 girls to school" },
  { Icon: Users, title: "Women's Leadership Academy", raised: 42, goal: "Train 500 future leaders" },
  { Icon: Sprout, title: "Community Development", raised: 55, goal: "Empower 30 rural communities" },
];

const AMOUNTS = [
  { key: "donate_10", label: "$10" },
  { key: "donate_25", label: "$25" },
  { key: "donate_50", label: "$50" },
  { key: "donate_100", label: "$100" },
  { key: "donate_250", label: "$250" },
];

export default function Support() {
  const [selected, setSelected] = useState("donate_25");
  const [recurring, setRecurring] = useState(false);
  const [loading, setLoading] = useState(false);

  const donate = async () => {
    setLoading(true);
    const lookup = recurring ? "donate_monthly_25" : selected;
    try { await startCheckout(lookup); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout"); setLoading(false); }
  };

  return (
    <div className="section py-12 sm:py-16" data-testid="support-page">
      <p className="kicker mb-2">Charitable Giving</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl text-amber-50">Support the Movement</h1>
      <p className="mt-3 max-w-2xl text-amber-100/60">Fuel education, leadership and community initiatives across the continent. Charitable donations are kept separate from commercial sponsorship.</p>

      <div className="mt-10 grid gap-4 lg:grid-cols-3">
        {CAMPAIGNS.map((c) => (
          <div key={c.title} className="card-lux p-6" data-testid={`campaign-${c.title.replace(/\s+/g, '-').toLowerCase()}`}>
            <c.Icon className="mb-3 h-6 w-6 text-gold" />
            <h3 className="font-serif text-xl text-amber-50">{c.title}</h3>
            <p className="mt-1 text-sm text-amber-100/60">{c.goal}</p>
            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-obsidian-600">
              <div className="h-full gold-bg" style={{ width: `${c.raised}%` }} />
            </div>
            <p className="mt-2 font-mono text-xs text-gold">{c.raised}% funded</p>
          </div>
        ))}
      </div>

      <div className="mt-12 glass rounded-2xl p-8">
        <h2 className="text-2xl text-amber-50 flex items-center gap-2"><Heart className="h-5 w-5 text-gold" /> Make a Contribution</h2>
        <div className="mt-6 flex flex-wrap gap-3">
          {AMOUNTS.map((a) => (
            <button key={a.key} onClick={() => { setSelected(a.key); setRecurring(false); }} data-testid={`donate-${a.key}`}
              className={`rounded-full px-5 py-2.5 font-mono text-sm transition-all ${selected === a.key && !recurring ? "gold-bg text-obsidian" : "border border-gold/25 text-amber-50/70 hover:border-gold/60"}`}>{a.label}</button>
          ))}
        </div>
        <label className="mt-5 flex items-center gap-2 text-sm text-amber-100/70">
          <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} className="accent-[#E6005C]" data-testid="recurring-toggle" /> Give $25 monthly instead
        </label>
        <button onClick={donate} disabled={loading} className="btn-gold mt-6 inline-flex items-center gap-2" data-testid="donate-submit">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Donate {recurring ? "$25/mo" : AMOUNTS.find((a) => a.key === selected)?.label}
        </button>
        <p className="mt-3 text-xs text-muted-foreground">Secure checkout by Stripe. Test card: 4242 4242 4242 4242, any future expiry & CVC.</p>
      </div>
    </div>
  );
}
